import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { AddTradeForm } from "./AddTradeForm";

export type OpenDrawerOptions = {
  onClose?: () => void;
};

type AddTradeDrawerContextValue = {
  isOpen: boolean;
  open: (options?: OpenDrawerOptions) => void;
  close: () => void;
};

const AddTradeDrawerContext = createContext<AddTradeDrawerContextValue | null>(null);

function isMacLike(): boolean {
  if (typeof navigator === "undefined") return false;
  const candidate = (navigator.platform ?? "") + " " + (navigator.userAgent ?? "");
  return /Mac|iPhone|iPad|iPod/i.test(candidate);
}

export type AddTradeDrawerProviderProps = {
  children: ReactNode;
  onTradeCreated?: (tradeId: string) => void;
};

export function AddTradeDrawerProvider({
  children,
  onTradeCreated,
}: AddTradeDrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const onCloseRef = useRef<(() => void) | null>(null);

  const open = useCallback((options?: OpenDrawerOptions) => {
    onCloseRef.current = options?.onClose ?? null;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (!nextOpen) {
      const fn = onCloseRef.current;
      onCloseRef.current = null;
      fn?.();
    }
  }, []);

  const handleCreated = useCallback(
    (tradeId: string) => {
      onCloseRef.current = null;
      setIsOpen(false);
      onTradeCreated?.(tradeId);
    },
    [onTradeCreated],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const mod = isMacLike() ? event.metaKey : event.ctrlKey;
      if (!mod) return;
      if (event.key.toLowerCase() !== "n") return;
      if (event.shiftKey || event.altKey) return;
      event.preventDefault();
      open();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const value = useMemo<AddTradeDrawerContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return (
    <AddTradeDrawerContext.Provider value={value}>
      {children}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 left-auto top-0 bottom-0 grid h-full max-h-screen w-full max-w-xl translate-x-0 translate-y-0 grid-rows-[auto_1fr] gap-0 overflow-hidden rounded-none border-l p-0 sm:rounded-none"
        >
          <DialogHeader className="border-b border-outline-variant px-6 py-4">
            <DialogTitle>New trade</DialogTitle>
            <DialogDescription>Add a trade and its first leg.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4">
            <AddTradeForm onCreated={handleCreated} onCancel={close} />
          </div>
        </DialogContent>
      </Dialog>
    </AddTradeDrawerContext.Provider>
  );
}

export function useAddTradeDrawer(): AddTradeDrawerContextValue {
  const ctx = useContext(AddTradeDrawerContext);
  if (ctx === null) {
    throw new Error("useAddTradeDrawer must be used inside <AddTradeDrawerProvider>");
  }
  return ctx;
}
